import { castToNumber } from 'util/numbers'
import { deepFreeze, deepCopy } from 'util/objects'

export const FORM_KEY_ORDER_ITEMS = 'orderItems'
export const FORM_KEY_HAS_TAXES = 'hasTaxes'
export const FORM_KEY_DISCOUNT = 'appliedDiscount'
export const FORM_KEY_SHIPPING = 'shipping'
export const FORM_KEY_NOTES = 'notes'
export const FORM_KEY_TAGS = 'tags'
export const FORM_KEY_IS_PAYMENT_DUE_LATER = 'isPaymentDueLater'
export const FORM_KEY_PAYMENT_TERMS_TEMPLATE_ID = 'paymentTermsTemplateId'
export const FORM_KEY_PAYMENT_TERMS_TYPE = 'paymentTermsType'
export const FORM_KEY_PAYMENT_SCHEDULES_DUE_AT = 'dueAt'
export const FORM_KEY_PAYMENT_SCHEDULES_ISSUED_AT = 'issuedAt'

export const NEW_ORDER_DEFAULTS = deepFreeze({
  [FORM_KEY_ORDER_ITEMS]: [],
  [FORM_KEY_HAS_TAXES]: false,
  [FORM_KEY_SHIPPING]: {},
  [FORM_KEY_DISCOUNT]: null,
  [FORM_KEY_NOTES]: '',
  [FORM_KEY_TAGS]: '',
  [FORM_KEY_IS_PAYMENT_DUE_LATER]: false,
  [FORM_KEY_PAYMENT_TERMS_TYPE]: null,
  [FORM_KEY_PAYMENT_TERMS_TEMPLATE_ID]: null,
  [FORM_KEY_PAYMENT_SCHEDULES_DUE_AT]: null,
  [FORM_KEY_PAYMENT_SCHEDULES_ISSUED_AT]: null,
})

const getPrice = obj => {
  if (obj.discountedUnitPriceSet) {
    return castToNumber(obj?.discountedUnitPriceSet?.shopMoney?.amount)
  } else if (obj.variant) {
    return obj.variant.price
  }
  return 0
}

export const generateDuplicateOrder = (order, priceCurrency) => {
  const totalTaxes = order.currentTotalTaxSet?.shopMoney?.amount

  const existingOrderDefaults = {
    ...deepCopy(NEW_ORDER_DEFAULTS),
    [FORM_KEY_SHIPPING]: { type: 'free' },
    [FORM_KEY_NOTES]: order.note,
    [FORM_KEY_TAGS]: order.tags ? order.tags.join(',') : '',
    // NOTE: shopify does not copy over payment terms & schedules on order duplication
    // NOTE: shopify does not copy over discount on order duplication
  }

  const taxes = castToNumber(totalTaxes)
  if (taxes > 0) existingOrderDefaults[FORM_KEY_HAS_TAXES] = true

  const lineItems = order.lineItems?.edges || []
  lineItems.forEach(obj => {
    const item = obj.node
    const variant = item.variant
    if (!variant) {
      const quantity = item.currentQuantity || 1
      const price = getPrice(item)
      const newItem = {
        ...item,
        custom: true,
        displayName: item.name,
        title: item.name,
        product: {
          name: item.name,
        },
        customPrice: price,
        price: {
          price,
          currency: priceCurrency,
        },
        quantity,
        total: price * quantity,
      }
      existingOrderDefaults.orderItems.push(newItem)
      return newItem
    }
    const price = getPrice(item)
    const quantity = item.currentQuantity || 1
    const total = price * quantity
    const newItem = {
      ...item,
      variantId: variant.id,
      product: {
        name: variant.displayName,
        stock: variant.inventoryQuantity,
        sku: variant.sku,
      },
      price: {
        price: price * 100, // all prices we get are with decimals
        currency: priceCurrency,
      },
      quantity,
      total: total * 100,
    }
    existingOrderDefaults.orderItems.push(newItem)
    return newItem
  })

  return existingOrderDefaults
}

// Well shit, line items requires a variantId to be stored
// in the variantId field (duh...), but for some reasons our
// internal structure somtimes stores the variantId in the variantId
// and somtimes it stores it in the id
const hackVariantIdFromOrderItem = orderItem => {
  const { id, variantId } = orderItem

  if (id.includes('ProductVariant')) return id
  return variantId
}

const convertOrderItemToLineItem = orderItem => {
  let item
  if (orderItem.custom) {
    item = {
      title: orderItem.displayName,
      quantity: +orderItem.quantity,
      originalUnitPrice: +orderItem.customPrice,
      taxable: orderItem.taxable,
      requiresShipping: orderItem.requiresShipping,
    }
  } else {
    item = {
      quantity: +orderItem.quantity,
      variantId: hackVariantIdFromOrderItem(orderItem),
      requiresShipping: orderItem.requiresShipping,
    }
  }

  item.appliedDiscount = orderItem.appliedDiscount
  return item
}

export const convertOrderItemsToLineItems = orderItems => {
  return orderItems.map(convertOrderItemToLineItem)
}

export const generateDefaultValues = (order, priceCurrency) => {
  if (!order) return deepCopy(NEW_ORDER_DEFAULTS)

  return generateDuplicateOrder(order, priceCurrency)
}
